import React from "react";
import ScreenerHeader from "./ScreenerHeader";
import ScreenerFooter from "./ScreenerFooter";
import SectionHeader from "./SectionHeader";
import QuestionCard from "./QuestionCard";
import {
  CAREGIVER_SECTION_ITEMS,
  SECTION_COUNT,
  SECTION_DEFINITIONS,
  SECTION_PSC_ITEMS
} from "./sections";
import {
  CaregiverAnswers,
  LIKERT_OPTIONS,
  LikertAnswer,
  PSC_OPTIONS,
  PscAnswer,
  PscAnswers
} from "./scoring";

type SectionScreenProps = {
  readonly index: number;
  readonly pscAnswers: PscAnswers;
  readonly caregiverAnswers: CaregiverAnswers;
  readonly onPscAnswer: (id: string, value: PscAnswer) => void;
  readonly onCaregiverAnswer: (id: string, value: LikertAnswer) => void;
  readonly onBack: () => void;
  readonly onNext: () => void;
};

export default function SectionScreen({
  index,
  pscAnswers,
  caregiverAnswers,
  onPscAnswer,
  onCaregiverAnswer,
  onBack,
  onNext
}: SectionScreenProps) {
  const section = SECTION_DEFINITIONS[index];
  const isLikert = section.scale === "likert";
  const items = isLikert ? CAREGIVER_SECTION_ITEMS : SECTION_PSC_ITEMS[index];

  const answered = isLikert
    ? items.every(item => caregiverAnswers[item.id] !== undefined)
    : items.every(item => pscAnswers[item.id] !== undefined);

  const percentComplete = Math.round((index / SECTION_COUNT) * 100);

  return (
    <section className="screener-step screener-step--section" aria-label={`Section ${index + 1} of ${SECTION_COUNT}`}>
      <ScreenerHeader />

      <div className="screener-progress">
        <div className="screener-progress__labels">
          <span className={`screener-progress__step screener-progress__step--${section.theme}`}>
            SECTION {index + 1} OF {SECTION_COUNT}
          </span>
          <span className="screener-progress__percent">{percentComplete}% complete</span>
        </div>
        <div className="screener-progress__track">
          <div
            className={`screener-progress__bar screener-progress__bar--${section.theme}`}
            style={{ width: `${percentComplete}%` }}
          />
        </div>
      </div>

      <SectionHeader theme={section.theme} title={section.title} prompt={section.prompt} />

      <div className="screener-questions">
        {isLikert
          ? items.map(item => (
              <QuestionCard<LikertAnswer>
                key={item.id}
                theme={section.theme}
                question={item.text}
                options={LIKERT_OPTIONS}
                value={caregiverAnswers[item.id]}
                onSelect={value => onCaregiverAnswer(item.id, value)}
              />
            ))
          : items.map(item => (
              <QuestionCard<PscAnswer>
                key={item.id}
                theme={section.theme}
                question={item.text}
                options={PSC_OPTIONS}
                value={pscAnswers[item.id]}
                onSelect={value => onPscAnswer(item.id, value)}
              />
            ))}
      </div>

      <div className="screener-nav">
        <button type="button" className="screener-button screener-button--secondary" onClick={onBack}>
          ← Back
        </button>
        <button
          type="button"
          className={`screener-button screener-button--${section.theme}`}
          disabled={!answered}
          onClick={onNext}
        >
          {section.nextLabel}
        </button>
      </div>

      <ScreenerFooter />
    </section>
  );
}
